import * as vp from 'vopay';
import { Table, Box, SubTitle, Badge } from 'vopay';
import { FunctionComponent } from 'react';

type TransactionInfoProps = {
  transaction: vp.EftFundingTransactionResponse;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | React.JSX.Element;
};

type TablePropsHandlers = {
  transaction: () => vp.TableProps;
  address: () => vp.TableProps;
  banking: () => vp.TableProps;
};

const EftFundingTransactionInfo: FC = ({ transaction }) => {
  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle="Transaction Detail" />
      </Box>
      <Table {...getTableProps('transaction')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle="Name and Address" />
      </Box>
      <Table {...getTableProps('address')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle="Banking Information" />
      </Box>
      <Table {...getTableProps('banking')} />
    </div>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transaction: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: 'Transaction ID',
            value: transaction.TransactionID,
          },
          {
            display: 'Transaction Status',
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(transaction.TransactionStatus ?? '')}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: 'Transaction Date Time',
            value: transaction.TransactionDateTime,
          },
          {
            display: 'Hold Release Date Time',
            value: transaction.HoldReleaseDateTime,
          },
          {
            display: 'Amount',
            value: transaction.Amount,
          },
          {
            display: 'Currency',
            value: transaction.Currency,
          },
          {
            display: 'Hold Amount',
            value: transaction.HoldAmount,
          },
          {
            display: 'Scheduled Transaction ID',
            value: transaction.ScheduledTransactionID,
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Transaction Detail' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return accountInfoProps;
      },
      address: () => {
        const addressInfoPairs: KeyValuePair[] = [
          {
            display: 'First Name',
            value: transaction.FirstName,
          },
          {
            display: 'Last Name',
            value: transaction.LastName,
          },
          {
            display: 'Comany Name',
            value: transaction.CompanyName,
          },
          {
            display: 'DOB',
            value: transaction.DOB,
          },
          {
            display: 'Phone Number',
            value: transaction.PhoneNumber,
          },
          {
            display: 'Email',
            value: transaction.Email,
          },
          {
            display: 'Address 1',
            value: transaction.Address1,
          },
          {
            display: 'Address 2',
            value: transaction.Address2,
          },
          {
            display: 'City',
            value: transaction.City,
          },
          {
            display: 'Province',
            value: transaction.Province,
          },
          {
            display: 'Country',
            value: transaction.Country,
          },
          {
            display: 'Postal Code',
            value: transaction.PostalCode,
          },
        ];

        const addressInfoProps: vp.TableProps = {
          name: 'account-address',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Address' },
            detailValue: { header: '' },
          },
          rows: addressInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return addressInfoProps;
      },
      banking: () => {
        const bankingInformationPairs: KeyValuePair[] = [
          {
            display: 'Financial Institution Number',
            value: transaction.FinancialInstitutionNumber,
          },
          {
            display: 'Branch Transit Number',
            value: transaction.BranchTransitNumber,
          },
          {
            display: 'Account Number',
            value: transaction.AccountNumber,
          },
          {
            display: 'Client Reference Number',
            value: transaction.ClientReferenceNumber,
          },
        ];

        const bankingInformationProps: vp.TableProps = {
          name: 'banking-information',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Banking Information' },
            detailValue: { header: '' },
          },
          rows: bankingInformationPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return bankingInformationProps;
      },
    };

    return handlers[handler]();
  }
};

export default EftFundingTransactionInfo;
